// Type-only: keeps this module importable by the structural tests, which run with no
// npm install and therefore cannot resolve a runtime import of a workspace package.
import type { CinemaBlogDto } from '@cineamo/api/models/cinema-blog/CinemaBlogDto.types';

export function slugify(text: string): string {
    return text
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]+/g, '');
}

export function getBlogPostUrl(cinemaBlog: CinemaBlogDto): string {
    return `post-${cinemaBlog.id}-${slugify(cinemaBlog.title)}`;
}

/**
 * Strips a scheme and a leading `www.` off a configured domain, leaving the bare
 * host a configuration is stored under.
 */
export function normalizeConfiguredDomain(domain: string): string {
    // Lowercase first: stripping `www.` before folding case leaves a `WWW.` in place,
    // which a second pass would then remove -- so the order is what makes this
    // idempotent, and both call sites feed it values that have already been through it.
    return domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/+$/, '');
}

/**
 * The base URL to show a human for a configured domain: the domain exactly as stored,
 * with nothing guessed.
 *
 * Whether a `www.` variant exists is a per-domain Vercel configuration, and label
 * counting cannot know it — `coyotevsacme.capelight.de` (three labels) is served at
 * `www.`, while `the-batman-2.movie.cineamo.test` (four) is not, and an apex-only
 * domain would have no `www.` at all. Guessing therefore risks printing a host with no
 * certificate, which simply does not resolve.
 *
 * The stored domain cannot be wrong: it is what the landing page itself is reached
 * through, so it is configured by definition. If a `www.` variant is the canonical one,
 * Vercel answers the stored host with a 307 that keeps the path (verified against
 * production), so the URL still lands on the file — one redirect hop, never a dead link.
 *
 * Use this for anything a person copies. For calling the deployment back (revalidation)
 * prefer {@link publicBaseUrlForDomain}: a redirect there means an authenticated POST
 * crossing hosts, which is worth avoiding.
 */
export function configuredDomainUrl(domain: string): string {
    const host = normalizeConfiguredDomain(domain);
    return host ? `https://${host}` : '';
}

/**
 * The host to call a deployment back on — revalidation and anything else where a
 * redirect is undesirable, because following one would mean an authenticated POST
 * crossing to a different host.
 *
 * A bare domain (`some-movie.de`) is assumed to be served at `www.`, a subdomain
 * (`the-batman-2.movie.cineamo.test`, `film.cineamo.dev`) is not — our wildcard
 * certificates cover a single label, so a `www.` variant of one of those has no
 * certificate.
 *
 * This is an assumption, not a fact: whether `www.` exists is per-domain Vercel
 * configuration (`coyotevsacme.capelight.de` has three labels and *is* served at
 * `www.`). It is kept because it is the long-standing behaviour of the two call sites
 * below and changing it would change which host we purge. **Do not use it for a URL a
 * person copies** — use {@link configuredDomainUrl}, which guesses nothing.
 *
 * Counting labels is how this has always been decided here (see
 * `revalidateGetCinema` in `@cineamo/api/helpers/revalidation-helper` and the movie
 * landing page save handler, both of which apply the same rule). It is deliberately
 * kept identical to those rather than made smarter: a multi-label public suffix such
 * as `example.co.uk` is treated as a subdomain and left without `www.`. Changing that
 * would need a public-suffix list and would have to change all three call sites
 * together, or the URL an admin copies would stop matching the URL we purge.
 */
export function publicBaseUrlForDomain(domain: string): string {
    const host = normalizeConfiguredDomain(domain);
    if (!host) {
        return '';
    }
    const hasSubdomain = host.split('.').length > 2;
    return hasSubdomain ? `https://${host}` : `https://www.${host}`;
}
